import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { alpha, useTheme } from '@mui/material/styles';

import { useAlertHistoryQuery } from '../api/queries';
import SeverityChip from '../components/SeverityChip';
import { useErrorSnackbar } from '../components/ErrorSnackbar';
import type { AlertHistoryEntry } from '../data/types';

type SeverityLevel = 'critical' | 'warning' | 'info';

const TIME_OPTIONS = [
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
  { label: '30d', value: 720 },
  { label: '90d', value: 2160 },
] as const;

/** Map backend severity strings to SeverityChip values */
function mapSeverity(severity: string): SeverityLevel {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'critical';
    case 'medium':
    case 'warning':
      return 'warning';
    default:
      return 'info';
  }
}

export default function AlertHistoryPage() {
  const theme = useTheme();
  const { showError } = useErrorSnackbar();
  const [hours, setHours] = useState(168);
  const [selectedAlert, setSelectedAlert] = useState<AlertHistoryEntry | null>(null);

  const { data: alerts, isLoading, isError } = useAlertHistoryQuery(hours);

  useEffect(() => {
    if (isError) {
      showError('Failed to load alert history');
    }
  }, [isError, showError]);

  // Clear selection when time filter changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: resets UI selection when time filter changes
    setSelectedAlert(null);
  }, [hours]);

  const columns: GridColDef<AlertHistoryEntry>[] = [
    {
      field: 'alertSentAt',
      headerName: 'Date/Time',
      flex: 1.5,
      minWidth: 170,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8rem',
          }}
        >
          {new Date(params.value as string).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const recipients = params.value as string[];
        return (
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.8rem',
              color: 'text.secondary',
            }}
          >
            {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
          </Typography>
        );
      },
    },
    {
      field: 'totalClientCount',
      headerName: 'Clients',
      flex: 0.5,
      minWidth: 70,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            fontWeight: 600,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'totalIssueCount',
      headerName: 'Issues',
      flex: 0.5,
      minWidth: 70,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: (params.value as number) > 0 ? 'error.main' : 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'severity',
      headerName: 'Severity',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => {
        const mapped = mapSeverity(params.value as string);
        return <SeverityChip severity={mapped} label={params.value as string} />;
      },
    },
    {
      field: 'auditRunId',
      headerName: 'Audit Run',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }}
        >
          {(params.value as string).substring(0, 8)}
        </Typography>
      ),
    },
  ];

  if (isLoading && !alerts) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={40} width={300} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  const alertCount = alerts?.length ?? 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5">Alert History</Typography>
        <Chip
          label={`${alertCount} alert${alertCount !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}
        />
      </Box>

      {/* Toolbar */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={hours}
          exclusive
          onChange={(_, val) => {
            if (val !== null) setHours(val);
          }}
          size="small"
        >
          {TIME_OPTIONS.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              sx={{
                px: 2,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* DataGrid or empty state */}
      {alertCount === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
          <Typography variant="body1" color="text.secondary">
            No alerts sent in this time period
          </Typography>
        </Box>
      ) : (
        <>
          <DataGrid
            rows={alerts ?? []}
            columns={columns}
            getRowId={(row) => row.alertSentAt + row.auditRunId}
            density="compact"
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedAlert(params.row)}
            sx={(t) => ({
              cursor: 'pointer',
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 2.5,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: alpha(t.palette.primary.main, 0.04),
                ...t.applyStyles('light', {
                  bgcolor: alpha(t.palette.primary.main, 0.03),
                }),
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: '0.68rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 0.1s',
                '&:hover': {
                  bgcolor: alpha(t.palette.primary.main, 0.04),
                },
              },
              '& .MuiDataGrid-cell': {
                borderColor: t.palette.divider,
              },
            })}
          />

          {/* Selected alert detail */}
          {selectedAlert && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '0.95rem' }}>
                    Alert Details
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    {new Date(selectedAlert.alertSentAt).toLocaleString()}
                  </Typography>
                </Box>

                {/* Recipients */}
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.8rem' }}
                >
                  Recipients: {selectedAlert.recipients.join(', ')}
                </Typography>

                {/* Client summary breakdown */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {selectedAlert.clientSummary.map((client) => (
                    <Box
                      key={client.clientName}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                        {client.clientName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          {client.issueTypes.join(', ')}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            color:
                              client.issueCount >= 5
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                          }}
                        >
                          {client.issueCount} issue{client.issueCount !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
